import { cloneDeep } from 'lodash';
import type { PiniaPluginContext } from 'pinia';

// Type extension for stores with custom reset method
interface StoreWithCustomReset {
  resetCustomStore?: () => void;
}

/**
 * @description: Pinia plugin that automatically adds $reset() action to all stores
 * This plugin intelligently handles:
 * - Simple stores with basic reactive state
 * - Complex stores with useStorage, readonly refs, or custom reset logic
 * - Stores that provide their own resetCustomStore() method
 * @author: Omar Sánchez Martinez
 * @since: 18/06/2025
 */
export function piniaResetPlugin({ store }: PiniaPluginContext) {
  // Capture the initial state when the store is created
  const initialState = cloneDeep(store.$state);

  /**
   * @description: Resets the store to its initial state
   * Priority: 1) Custom resetCustomStore() method, 2) Generic $patch reset
   * @author: Omar Sánchez Martinez
   * @since: 18/06/2025
   */
  store.$reset = () => {
    try {
      // Check if the store has a custom reset method
      const storeWithCustomReset = store as StoreWithCustomReset;
      if (typeof storeWithCustomReset.resetCustomStore === 'function') {
        console.debug(`Using custom reset for store: ${store.$id}`);
        storeWithCustomReset.resetCustomStore();
        return;
      }

      // Fallback to generic reset for simple stores
      console.debug(`Using generic reset for store: ${store.$id}`);
      store.$patch(cloneDeep(initialState));
    } catch (error) {
      console.warn(`Error in store $reset for "${store.$id}":`, error);

      // Final fallback: try to reset individual properties
      try {
        for (const [key, initialValue] of Object.entries(initialState)) {
          try {
            store.$state[key] = cloneDeep(initialValue);
          } catch (propError) {
            console.warn(`Failed to reset property "${key}" in store "${store.$id}":`, propError);
          }
        }
      } catch (fallbackError) {
        console.warn(
          `Complete failure in store $reset fallback for "${store.$id}":`,
          fallbackError,
        );
      }
    }
  };

  return {};
}
